import { ACTION_PLAN_SOURCE } from 'constants/actionPlan';
import { PAGINATION } from 'constants/pagination';
import { SelectAdvancedFilterOptionType } from 'types/common';

export const ACTION_PLANS_PAGE_SIZE = PAGINATION.PAGE_SIZE;

const ACTION_PLAN_BASE_STATUS = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  COMPLETE: 3,
} as const;

export const ACTION_PLAN_STATUS = ACTION_PLAN_BASE_STATUS;

export const ACTION_PLAN_STATUS_LABEL: Record<number, string> = {
  [ACTION_PLAN_STATUS.NOT_STARTED]: 'Not Started',
  [ACTION_PLAN_STATUS.IN_PROGRESS]: 'In Progress',
  [ACTION_PLAN_STATUS.COMPLETE]: 'Complete',
};

export const ACTION_PLAN_STATUS_OPTIONS = [
  { label: ACTION_PLAN_STATUS_LABEL[ACTION_PLAN_STATUS.NOT_STARTED], value: ACTION_PLAN_STATUS.NOT_STARTED },
  { label: ACTION_PLAN_STATUS_LABEL[ACTION_PLAN_STATUS.IN_PROGRESS], value: ACTION_PLAN_STATUS.IN_PROGRESS },
  { label: ACTION_PLAN_STATUS_LABEL[ACTION_PLAN_STATUS.COMPLETE], value: ACTION_PLAN_STATUS.COMPLETE },
];

export const ACTION_PLAN_STATUS_BG_COLOR_MAP: Record<number, string> = {
  [ACTION_PLAN_STATUS.NOT_STARTED]: 'var(--background)',
  [ACTION_PLAN_STATUS.IN_PROGRESS]: 'var(--pale-blue)',
  [ACTION_PLAN_STATUS.COMPLETE]: 'var(--light-green)',
};

export const ACTION_PLAN_ITEM_TYPE_OPTIONS = [
  { id: 1, name: 'Operations' },
  { id: 2, name: 'Marketing' },
  { id: 3, name: 'Sales' },
];

export const ACTION_PLAN_ITEM_ACTION_MAX_LENGTH = 1024;
export const ACTION_PLAN_SAVE_ERROR_MESSAGE = 'Unable to save action plan. Please try again.';

export const ACTION_PLAN_TAB_ALL = 'all' as const;

export const ACTION_PLAN_TAB_QUERY_PARAM = 'tab';

export type ActionPlanTab = typeof ACTION_PLAN_TAB_ALL | ACTION_PLAN_SOURCE;

export const ACTION_PLAN_TAB_LABEL: Record<ActionPlanTab, string> = {
  [ACTION_PLAN_TAB_ALL]: 'All Plans',
  [ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE]: 'From AI Competitive Intelligence',
  [ACTION_PLAN_SOURCE.USER_ADDED]: 'From My Team',
  [ACTION_PLAN_SOURCE.ALERTS]: 'From Alerts',
};

export const ACTION_PLAN_TAB_DESCRIPTION: Record<ActionPlanTab, string> = {
  [ACTION_PLAN_TAB_ALL]:
    'Every action plan for this community, whatever created it — AI analysis, alerts, campaigns, or your own team.',
  [ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE]:
    'Plans the AI generated from your standing market position — competitive analysis across the seven scoring categories, email campaign performance, and lead response time. These are opportunities found for you, not urgent events.',
  [ACTION_PLAN_SOURCE.USER_ADDED]:
    'Plans you and your colleagues created yourselves, for work the AI has not flagged — process changes, training, and anything you want tracked alongside the generated plans.',
  [ACTION_PLAN_SOURCE.ALERTS]:
    'Plans created in response to something that just happened, based on the alert rules set in Settings — Competitor Incentive Updates, Market Rate Comparison Updates, and Community Review Alerts. These arrive frequently and are usually the most time-sensitive.',
};

export const ACTION_PLAN_TAB_ORDER: ActionPlanTab[] = [
  ACTION_PLAN_TAB_ALL,
  ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE,
  ACTION_PLAN_SOURCE.USER_ADDED,
  ACTION_PLAN_SOURCE.ALERTS,
];

export const ACTION_PLAN_DEFAULT_TAB: ActionPlanTab = ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE;

export const ACTION_PLAN_SOURCE_FILTER_EMAIL_MARKETING = 'email_marketing' as const;
export const ACTION_PLAN_SOURCE_FILTER_SPEED_TO_LEAD = 'speed_to_lead' as const;

export type ActionPlanSourceFilterValue =
  | ACTION_PLAN_SOURCE
  | typeof ACTION_PLAN_SOURCE_FILTER_EMAIL_MARKETING
  | typeof ACTION_PLAN_SOURCE_FILTER_SPEED_TO_LEAD;

const ACTION_PLAN_SOURCE_FILTER_OPTION = {
  ALERTS: { value: ACTION_PLAN_SOURCE.ALERTS, label: 'Alerts' },
  AI_COMPETITIVE_INTELLIGENCE: {
    value: ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE,
    label: 'AI Competitive Intelligence',
  },
  EMAIL_MARKETING: { value: ACTION_PLAN_SOURCE_FILTER_EMAIL_MARKETING, label: 'Email Marketing' },
  SPEED_TO_LEAD: { value: ACTION_PLAN_SOURCE_FILTER_SPEED_TO_LEAD, label: 'Speed to Lead' },
  CREATED_BY_TEAM: { value: ACTION_PLAN_SOURCE.USER_ADDED, label: 'Created by Team' },
};

export const ACTION_PLAN_SOURCE_FILTER_OPTIONS_BY_TAB: Partial<
  Record<ActionPlanTab, SelectAdvancedFilterOptionType[]>
> = {
  [ACTION_PLAN_TAB_ALL]: [
    ACTION_PLAN_SOURCE_FILTER_OPTION.ALERTS,
    ACTION_PLAN_SOURCE_FILTER_OPTION.AI_COMPETITIVE_INTELLIGENCE,
    ACTION_PLAN_SOURCE_FILTER_OPTION.EMAIL_MARKETING,
    ACTION_PLAN_SOURCE_FILTER_OPTION.SPEED_TO_LEAD,
    ACTION_PLAN_SOURCE_FILTER_OPTION.CREATED_BY_TEAM,
  ],
  [ACTION_PLAN_SOURCE.AI_COMPETITIVE_INTELLIGENCE]: [
    ACTION_PLAN_SOURCE_FILTER_OPTION.AI_COMPETITIVE_INTELLIGENCE,
    ACTION_PLAN_SOURCE_FILTER_OPTION.EMAIL_MARKETING,
    ACTION_PLAN_SOURCE_FILTER_OPTION.SPEED_TO_LEAD,
  ],
};

export const ACTION_PLAN_ALERT_TYPE_FILTER_GROUP = {
  INCENTIVE: 1,
  MARKET_RATE: 2,
  REVIEW: 3,
} as const;

export const ACTION_PLAN_ALERT_TYPE_FILTER_OPTIONS: SelectAdvancedFilterOptionType[] = [
  { value: ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.INCENTIVE, label: 'Incentive Alert' },
  { value: ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.MARKET_RATE, label: 'Market Rate Alert' },
  { value: ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.REVIEW, label: 'Review Alert' },
];

export const ACTION_PLAN_ALERT_TYPE_FILTER_GROUP_TO_IDS: Record<number, number[]> = {
  [ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.INCENTIVE]: [1],
  [ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.MARKET_RATE]: [2],
  [ACTION_PLAN_ALERT_TYPE_FILTER_GROUP.REVIEW]: [3, 4],
};
